import React, { useState, useRef, useEffect } from 'react';
import { FolderWithClasses } from '../classroom/folders/types';
import { motion } from 'framer-motion';
import { Folder as FolderIcon } from 'lucide-react';

interface FolderCardProps {
  folder: FolderWithClasses;
  onClick: () => void;
  isAdmin: boolean;
  onEdit: (e: React.MouseEvent) => void;
  onDelete: (e: React.MouseEvent) => void;
  index: number; // For staggered animation
}

const FolderCard: React.FC<FolderCardProps> = ({ 
  folder, 
  onClick, 
  isAdmin, 
  onEdit, 
  onDelete,
  index
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [tiltValues, setTiltValues] = useState({ x: 0, y: 0 });
  const [ripple, setRipple] = useState({ active: false, x: 0, y: 0 });
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check user's motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handleChange = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (prefersReducedMotion || !cardRef.current) return;
    
    const rect = cardRef.current.getBoundingClientRect();
    
    // Calculate mouse position relative to card center (range: -1 to 1)
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    
    // Set tilt values (max 5 degrees)
    setTiltValues({
      x: y * -5, // Invert Y for natural tilt
      y: x * 5
    });
  };

  const handleCardClick = (e: React.MouseEvent) => {
    if (cardRef.current) {
      // Create ripple effect
      const rect = cardRef.current.getBoundingClientRect();
      setRipple({
        active: true,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
      
      // Start transition to new page after ripple animation
      setTimeout(() => {
        onClick();
      }, 300);
    }
  };

  // Calculate class count
  const classCount = 'class_count' in folder ? folder.class_count : 0;

  return (
    <motion.div
      ref={cardRef}
      className="relative overflow-hidden isolate"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ 
        duration: 0.4, 
        delay: prefersReducedMotion ? 0 : index * 0.04,
        ease: [0.23, 1, 0.32, 1]
      }}
      onClick={(e) => {
        if (!isAdmin || (e.target as HTMLElement).closest('button') === null) {
          handleCardClick(e);
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setTiltValues({ x: 0, y: 0 });
      }}
      onMouseMove={handleMouseMove}
      style={{
        transform: prefersReducedMotion 
          ? 'none' 
          : `perspective(1000px) rotateX(${tiltValues.x}deg) rotateY(${tiltValues.y}deg)`
      }}
    >
      {/* Card container */}
      <motion.div 
        className="bg-[#141C28]/60 rounded-xl backdrop-blur-lg backdrop-saturate-150 border border-white/5 flex flex-col items-center justify-center text-center h-full p-4"
        style={{ 
          transformStyle: 'preserve-3d',
          boxShadow: isHovered 
            ? '0 16px 20px -5px rgba(0, 0, 0, 0.2), 0 10px 15px -5px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(201, 255, 252, 0.2) inset' 
            : '0 8px 10px -6px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.1) inset',
          background: 'linear-gradient(135deg, rgba(20, 28, 40, 0.6), rgba(14, 22, 33, 0.4))',
          transition: 'box-shadow 140ms cubic-bezier(0.34, 1.56, 0.64, 1)'
        }}
        animate={{
          scale: ripple.active ? [1, 1.05, 1] : 1,
          transition: { duration: 0.4 }
        }}
      >
        {/* Ripple effect */}
        {ripple.active && !prefersReducedMotion && (
          <motion.div
            className="absolute rounded-full bg-[#c9fffc]/30 z-10"
            initial={{ 
              x: ripple.x, 
              y: ripple.y, 
              width: 0, 
              height: 0,
              opacity: 0.7
            }}
            animate={{ 
              width: 500, 
              height: 500,
              x: ripple.x - 250, 
              y: ripple.y - 250,
              opacity: 0
            }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          />
        )}

        {/* Image section - landscape format */}
        <div className="w-full aspect-video relative rounded-lg overflow-hidden mb-3 border border-white/10">
          {folder.img_url ? (
            <>
              <motion.div 
                className="absolute inset-0 bg-gradient-to-t from-[#0e1621]/80 via-transparent to-transparent z-10"
                animate={{ opacity: isHovered ? 1 : 0.7 }}
                transition={{ duration: 0.3 }}
              />
              <motion.img 
                src={folder.img_url} 
                alt={folder.name}
                className="w-full h-full object-cover"
                animate={{ 
                  scale: isHovered && !prefersReducedMotion ? 1.05 : 1
                }}
                transition={{ duration: 0.5 }}
                style={{ 
                  transformStyle: 'preserve-3d'
                }}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-[#1a2533]/80">
              <motion.div
                animate={{ 
                  scale: isHovered && !prefersReducedMotion ? [0.9, 1.1, 1] : 0.9,
                  rotateZ: isHovered ? [0, -5, 5, 0] : 0
                }}
                transition={{ duration: 0.5 }}
              >
                <FolderIcon className="w-20 h-20 text-[#c9fffc] opacity-50" />
              </motion.div>
            </div>
          )}
          
          {/* Class count badge */}
          <motion.div 
            className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-[#0e1621]/80 backdrop-blur-md text-xs font-medium border border-white/10 z-20"
            animate={{ 
              scale: isHovered ? 1.1 : 1, 
              backgroundColor: isHovered ? 'rgba(201, 255, 252, 0.2)' : 'rgba(14, 22, 33, 0.8)'
            }}
            transition={{ duration: 0.2 }}
          >
            {classCount} classes
          </motion.div>
        </div>

        {/* Content section with title */}
        <div className="w-full relative z-20">
          <motion.h3 
            className="text-lg font-medium text-white tracking-tight truncate mb-1"
            animate={{ 
              color: isHovered ? '#c9fffc' : '#ffffff'
            }}
            transition={{ duration: 0.3 }}
            style={{ 
              transformStyle: 'preserve-3d',
              transform: 'translateZ(5px)'
            }}
          >
            {folder.name}
          </motion.h3>
          
          <motion.div 
            className="text-xs text-gray-400"
            animate={{ opacity: isHovered ? 1 : 0.7 }}
            transition={{ duration: 0.2 }}
          >
            Category
          </motion.div>
        </div>
        
        {/* Admin controls - positioned over the image for better visibility */}
        {isAdmin && (
          <motion.div 
            className="absolute top-2 left-2 flex gap-2 z-30"
            initial={{ opacity: 0 }}
            animate={{ opacity: isHovered ? 1 : 0 }}
            transition={{ duration: 0.2 }}
            style={{ 
              transformStyle: 'preserve-3d',
              transform: 'translateZ(15px)'
            }}
          >
            <button 
              onClick={onEdit}
              className="p-1.5 bg-[#1a2533]/90 backdrop-blur-md rounded-lg hover:bg-blue-600 transition-colors border border-white/10"
            >
              <svg className="w-4 h-4 text-[#c9fffc]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            </button>
            <button 
              onClick={onDelete}
              className="p-1.5 bg-[#1a2533]/90 backdrop-blur-md rounded-lg hover:bg-red-500 group transition-colors border border-white/10"
            >
              <svg className="w-4 h-4 text-red-400 group-hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </button>
          </motion.div>
        )}
        
        {/* Progress bar (only shown if data exists) */}
        {(folder as any).completion_percentage !== undefined && (
          <div className="w-full mt-3 h-1 bg-gray-700 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-[#c9fffc]"
              initial={{ width: 0 }}
              animate={{ width: `${(folder as any).completion_percentage || 0}%` }}
              transition={{ duration: 0.6, delay: 0.2 }}
            />
          </div>
        )}
      </motion.div>
    </motion.div>
  );
};

export default FolderCard;
